// Policies - AI decision modules for the headless simulator.
//
// Each policy exposes two methods:
//
//   decidePlaying(view) → { moveX, moveY, attack, dash, super? }
//     Called every tick while the player is alive and a wave is running.
//     Must return a movement vector (will be normalized by SimInput) plus
//     `attack`/`dash` booleans. The simulator decides when those fire
//     (e.g. attack only if cooldown allows).
//
//   decideShop(view) → { action, index? }
//     Called once when an interwave or walk-in shop draft opens.
//     Supported actions: 'pick' (with index), 'reroll', 'skip'.
//
// The view object is a plain read-only snapshot the simulator builds each
// tick. Policies should not mutate it or hold references to entity
// instances across ticks.

import { randFn } from '../rng.js';
import { CONST } from '../utils.js';

function dist(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function nearestAlive(from, list) {
  let best = null;
  let bestD = Infinity;
  for (const e of list) {
    if (!e.alive) continue;
    const d = dist(from, e.pos);
    if (d < bestD) {
      bestD = d;
      best = e;
    }
  }
  return best ? { entity: best, distance: bestD } : null;
}

// Base class so custom policies can compose over shared helpers.
export class AIPolicy {
  constructor(name = 'base') {
    this.name = name;
  }
  decidePlaying(/* view */) {
    return { moveX: 0, moveY: 0, attack: false, dash: false };
  }
  decideShop(view) {
    if (!view.offer || view.offer.length === 0) return { action: 'skip' };
    // Pick the cheapest affordable card, or skip if broke.
    const costs = view.costs || [];
    let best = -1;
    for (let i = 0; i < view.offer.length; i++) {
      if ((costs[i] || 0) <= view.gold) { best = i; break; }
    }
    return best >= 0 ? { action: 'pick', index: best } : { action: 'skip' };
  }
}

// GreedyPolicy
//
// Priority:
//   1. If a guard is within `aggroRange`, kill it first.
//   2. Otherwise chase the nearest alive caravan.
//   3. Attack whenever target is inside melee reach.
//   4. Dash toward target if far and dash is off cooldown.
//   5. Always pick the first offered card.
//
// Deliberately dumb — we want a reproducible baseline for balance
// comparisons across builds. Smart heuristics belong in a separate policy.
export class GreedyPolicy extends AIPolicy {
  constructor(options = {}) {
    super(options.name || 'greedy');
    this.aggroRange = options.aggroRange ?? 220;
    this.dashDistance = options.dashDistance ?? 240;
    this.cardPick = options.cardPick || 'first'; // 'first' | 'random'
  }

  decidePlaying(view) {
    const p = view.player;

    // Priority order:
    //   1. Nearby guards (they hit us if we don't deal with them)
    //   2. Any alive caravan
    //   3. Remaining guards anywhere on the map
    //   4. Uncollected loot (so the wave can actually end)
    const nearestGuard = nearestAlive(p.pos, view.guards);
    const nearestCaravan = nearestAlive(p.pos, view.caravans);

    let target = null;
    let isLoot = false;
    if (nearestGuard && nearestGuard.distance < this.aggroRange) {
      target = nearestGuard;
    } else if (nearestCaravan) {
      target = nearestCaravan;
    } else if (nearestGuard) {
      target = nearestGuard;
    } else {
      // Nothing to fight — walk over to the nearest coin. `alive` on loot
      // means "not yet collected" so nearestAlive works directly.
      const loot = nearestAlive(p.pos, view.loots);
      if (loot) {
        target = loot;
        isLoot = true;
      }
    }

    if (!target) {
      return { moveX: 0, moveY: 0, attack: false, dash: false };
    }

    const dx = target.entity.pos.x - p.pos.x;
    const dy = target.entity.pos.y - p.pos.y;
    const d = target.distance;

    // Loot doesn't need swinging — just walk into it and let the magnet
    // pull it in. Otherwise, melee reach = attack.
    if (!isLoot) {
      const reach = p.radius + p.attackRange + (target.entity.radius || 12);
      if (d < reach * 0.95) {
        return { moveX: 0, moveY: 0, attack: true, dash: false };
      }
    }

    // Far away and dash is ready — close the gap quickly.
    const dash = !isLoot && d > this.dashDistance && p.dashCooldownTimer <= 0;

    return {
      moveX: d > 0 ? dx / d : 0,
      moveY: d > 0 ? dy / d : 0,
      attack: false,
      dash,
    };
  }

  decideShop(view) {
    if (!view.offer || view.offer.length === 0) return { action: 'skip' };
    const costs = view.costs || [];
    // Filter to affordable cards only.
    const affordable = [];
    for (let i = 0; i < view.offer.length; i++) {
      if ((costs[i] || 0) <= view.gold) affordable.push(i);
    }
    if (affordable.length === 0) return { action: 'skip' };
    let idx = affordable[0];
    if (this.cardPick === 'random') {
      const rand = randFn(view.rng);
      idx = affordable[Math.floor(rand() * affordable.length)];
    }
    return { action: 'pick', index: idx };
  }
}

// RandomCardPolicy — same movement as greedy, but randomly picks among
// the 5 offered cards. Useful for aggregate "which card tends to survive
// longer" analysis: run a batch, group by picked card id, compare
// median wave reached.
export class RandomCardPolicy extends GreedyPolicy {
  constructor(options = {}) {
    super({ ...options, name: options.name || 'random-cards', cardPick: 'random' });
  }
}

// PreferencePolicy — greedy movement plus a ranked card whitelist/blacklist.
//
// Construction:
//   new PreferencePolicy({ prefer: ['thorns','lifesteal'], avoid: ['glassCannon'] })
//
// Shop logic (in priority order):
//   1. Walk through `prefer` list. Return the first card that shows up in
//      the current offer AND isn't in `avoid`.
//   2. Otherwise pick the first offered card that isn't in `avoid`.
//   3. If everything is forbidden, skip the draft.
//
// This is the primary tool for balance experiments: force a specific
// build and see how far it reaches vs. a control run that excludes the
// same card.
export class PreferencePolicy extends GreedyPolicy {
  constructor(options = {}) {
    super({ ...options, name: options.name || 'preference' });
    this.prefer = options.prefer || [];
    this.avoid = new Set(options.avoid || []);
  }

  decideShop(view) {
    if (!view.offer || view.offer.length === 0) return { action: 'skip' };
    const costs = view.costs || [];
    const canAfford = (i) => (costs[i] || 0) <= view.gold;
    // 1. Highest-ranked preferred card present and affordable.
    for (const id of this.prefer) {
      if (this.avoid.has(id)) continue;
      const idx = view.offer.findIndex((c, i) => c.id === id && canAfford(i));
      if (idx >= 0) return { action: 'pick', index: idx };
    }
    // 2. First non-forbidden affordable card.
    const idx = view.offer.findIndex((c, i) => !this.avoid.has(c.id) && canAfford(i));
    if (idx >= 0) return { action: 'pick', index: idx };
    // 3. Nothing affordable or everything forbidden — skip.
    return { action: 'skip' };
  }
}

// SmartPolicy — greedy baseline that actually reaches late-game waves.
//
// Why it exists: plain GreedyPolicy dies on wave 4 (median), which collapses
// any late-game balance signal into noise. SmartPolicy layers four cheap
// heuristics on top so the same batch touches wave 10+ when card luck is
// reasonable. It's deliberately not optimal — just "a player who can see".
//
// Decision order per tick (first hit wins):
//   1. Arrow dodge. If a projectile is within ~90px and aimed at us,
//      sidestep perpendicular to its direction (dashes when ready).
//   2. Low HP retreat. Below `lowHpPct` of max HP, kite away from the
//      nearest guard and dash out if they're close.
//   3. Threat-weighted engage. Pick the most dangerous guard in aggro
//      range (archer > armored > basic, weighted by distance) instead of
//      the absolute nearest. Falls back to nearest caravan, then loot.
//   4. Strafe melee. In the neutral HP band, keep moving perpendicular
//      while swinging so melee guards can't stack hits.
//
// Shop: takes a small whitelist of sustain/offense cards in priority
// order and falls back to index 0 if none are offered.
export class SmartPolicy extends AIPolicy {
  constructor(options = {}) {
    super(options.name || 'smart');
    this.aggroRange = options.aggroRange ?? 260;
    this.dashDistance = options.dashDistance ?? 240;
    // HP bands (fractions of maxHp):
    //   [0 .. lowHpPct)   — retreat and kite
    //   [lowHpPct .. medHpPct) — strafe-attack melee guards (move + swing)
    //   [medHpPct .. 1]   — stand-and-swing
    // Retreating above lowHpPct hurts median wave (lost caravan income);
    // standing still below medHpPct makes melee trades lose HP faster.
    this.lowHpPct = options.lowHpPct ?? 0.3;
    this.medHpPct = options.medHpPct ?? 0.7;
    // HP threshold for arrow dodging. Above this fraction the AI ignores
    // incoming arrows and charges through — prevents infinite kiting of
    // archer clusters at full HP.
    this.arrowDodgeHpPct = options.arrowDodgeHpPct ?? 0.7;
    // Arrow dodge window. At PROJECTILE_SPEED=200, 90px ≈ 0.45s of
    // reaction time before the hit lands — enough room to dash or step.
    this.projectileWarnDist = options.projectileWarnDist ?? 90;
    this.projectileWarnTime = options.projectileWarnTime ?? 0.45;
    // Card priority. Sustain first, then offense, then mobility — picked
    // so the policy has a chance to heal between fights instead of
    // stacking glass cannon. Order kept stable from v1.2 to preserve the
    // baseline (changing it shifted smart's p50 from 11 to 9). v1.3.0
    // additions (fortify, armorBreak, cleave, crit, phantom) sit at the
    // bottom so they get picked via fallback when nothing better is on
    // offer — they're situational keystone cards, not auto-includes.
    this.preferCards = options.prefer || [
      'lifesteal', 'regen', 'maxHp', 'damage', 'thorns', 'dashCooldown', 'attackRange',
      'fortify', 'armorBreak', 'cleave', 'crit', 'phantom', 'doubleStrike',
    ];
    // Minimum gold needed before we'll detour to the in-world shop. Set
    // just above the cheapest common card cost so the trip doesn't fizzle
    // when the offer happens to be all uncommons / rares.
    this.shopMinGold = options.shopMinGold ?? 15;
    // Hard cap on shop trips per wave — the walk-in shop only allows one
    // pick anyway, so revisiting after spending is wasted travel.
    this._shopTripWave = -1;
    this._shopTripDone = false;
    // Cards the policy refuses to buy. Used by balance experiments to
    // measure "what does smart play look like if X were banned" without
    // having to drop down to GreedyPolicy's dumb movement.
    this.avoid = new Set(options.avoid || []);

    // Tunables lifted from hard-coded constants for the player-brain
    // evolution (~/dev/corovans-ai, policy `psmart`). Defaults reproduce the
    // hand-tuned policy exactly.
    this.fleeRange = options.fleeRange ?? this.aggroRange;
    this.swarmRadius = options.swarmRadius ?? 90;
    this.swarmCount = options.swarmCount ?? 2;
    this.swarmHpPct = options.swarmHpPct ?? 0.5;
    this.fleeDashDist = options.fleeDashDist ?? 160;
    this.reachFrac = options.reachFrac ?? 0.95;
    this.threatWeights = {
      archer: options.archerW ?? 2.5,
      armored: options.armoredW ?? 1.5,
      lancer: options.lancerW ?? 1,
      bombardier: options.bombardierW ?? 1,
    };
    this.threatDistFloor = options.threatDistFloor ?? 40;
    // Behaviours the hand-tuned policy doesn't have — all off by default.
    //   resumeHpPct  once retreating, keep retreating until HP ≥ this
    //                (or resumeGap: lowHpPct + gap)
    //   spacing      fight from the edge of our longer reach (we hit from
    //                ~47 px, a basic guard from ~32): close in and swing when
    //                ready, back out of his reach while recharging
    //   spacingMargin  px kept outside the guard's reach
    //   punishWindow  s — step inside his reach while his swing recharges
    //                for longer than this (0 = never)
    //   woundedBias  target score × (maxHp / hp)^bias — finish wounded guards
    //   tangentFlee  0..1 — mix the flee direction with a sideways one (circle)
    //   wallRepel    px — steer away from walls this close when fleeing
    //   dashReserve  no dashes for travel; keep the dash for threats
    //   medkitHpPct, pickupRange  below this HP, detour to a medkit this close
    //   stickyDodge  always dodge a sticky arrow (it glues us 2.2 s: half speed,
    //                no dash), at any HP, and prefer it over plain arrows
    //   fireDodge, fireRefreshSec  dodge a fire arrow at any HP unless we are
    //                already burning for more than fireRefreshSec — burn doesn't
    //                stack (a second arrow only refreshes it to 4 s), so dodging
    //                while on fire buys little. Burn ignores i-frames.
    // resumeGap (> 0) is the GA-friendly form: resume = lowHpPct + gap, so
    // no value of the gene is a silent "off" below lowHpPct.
    this.resumeHpPct = options.resumeHpPct ?? (options.resumeGap > 0 ? this.lowHpPct + options.resumeGap : 0);
    this.spacing = !!options.spacing;
    this.spacingMargin = options.spacingMargin ?? 6;
    this.punishWindow = options.punishWindow ?? 0;
    this.woundedBias = options.woundedBias ?? 0;
    this.tangentFlee = options.tangentFlee ?? 0;
    this.wallRepel = options.wallRepel ?? 0;
    this.dashReserve = !!options.dashReserve;
    this.medkitHpPct = options.medkitHpPct ?? 0;
    this.pickupRange = options.pickupRange ?? 0;
    this.stickyDodge = !!options.stickyDodge;
    this.fireDodge = !!options.fireDodge;
    this.fireRefreshSec = options.fireRefreshSec ?? 0;
    // Super strike + horse — on by default, the way a player would use them:
    //   superMinGuards  fire the super once this many guards are within its
    //                   radius (or any, below lowHpPct); 0 = never
    //   horseRange      px — walk to a horse drop this close while not riding
    //                   (0 = only grab one by walking over it)
    this.superMinGuards = options.superMinGuards ?? 3;
    this.horseRange = options.horseRange ?? 150;
    this._retreating = false;
  }

  decidePlaying(view) {
    const action = this._decideMove(view);
    if (action && this._wantsSuper(view)) action.super = true;
    return action;
  }

  _wantsSuper(view) {
    const p = view.player;
    if (!p.superReady || this.superMinGuards <= 0) return false;
    const near = this._guardsWithin(p, view.guards, CONST.SUPER_RADIUS);
    if (near >= this.superMinGuards) return true;
    return near > 0 && p.maxHp > 0 && p.hp / p.maxHp < this.lowHpPct;
  }

  _decideMove(view) {
    const p = view.player;
    const hpPct = p.maxHp > 0 ? p.hp / p.maxHp : 1;

    const threatGuard = this._mostThreateningGuard(p, view.guards);

    // 0. Blast evasion. Bomb damage bypasses i-frames, so unlike every
    // other threat there's no dodge-through option — the only answer is to
    // leave the circle before the fuse runs out. Takes priority over
    // everything else, including retreat, because standing in a marked
    // blast is strictly worse than any position outside it.
    const blast = this._threateningBomb(view.bombs, p);
    if (blast) {
      const melee = this._meleeTargetInReach(p, view.guards, view.caravans);
      return {
        moveX: blast.x,
        moveY: blast.y,
        attack: !!melee,
        // Dash is the fastest way out — but it's locked while stuck, so
        // don't burn the request when the answer would be "no".
        dash: p.dashCooldownTimer <= 0 && !p.stickyTimer,
      };
    }

    // 0.5. Lancer charge. The lunge is telegraphed for the whole windup
    // and locks its direction on release, so the counter is to step out of
    // the lane. Modelled at any HP (unlike arrows) because a charge hits
    // for ~1.5× a normal swing and knocks us back into the pack.
    const lunge = this._incomingCharge(view.guards, p);
    if (lunge) {
      return {
        moveX: -lunge.dir.y,
        moveY: lunge.dir.x,
        attack: !!this._meleeTargetInReach(p, view.guards, view.caravans),
        dash: p.dashCooldownTimer <= 0 && !p.stickyTimer,
      };
    }

    // 1. Arrow dodge — sidestep perpendicular to an incoming arrow; dash
    // when ready. But only below the confident HP threshold — at high HP
    // it's better to tank the ~6 damage and keep closing the gap rather
    // than endlessly kiting a cluster of archers.
    const incoming = this._incomingProjectile(view.projectiles, p);
    if (incoming && (hpPct < this.arrowDodgeHpPct || this._mustDodge(incoming, p))) {
      const perpX = -incoming.dir.y;
      const perpY = incoming.dir.x;
      const canDash = p.dashCooldownTimer <= 0 && !p.stickyTimer;
      const melee = this._meleeTargetInReach(p, view.guards, view.caravans);
      return {
        moveX: perpX,
        moveY: perpY,
        attack: !!melee,
        dash: canDash,
      };
    }

    // 2. Retreat mode — kite away from the cluster, dash out if anyone
    // is close. Two triggers:
    //   a) HP dropped below `lowHpPct` (absolute danger), or
    //   b) HP below half AND multiple guards are already on top of us,
    //      because one more trade will put us in trigger (a) with less
    //      room to recover.
    // Kiting uses the guard-cluster centroid (weighted by 1/dist) instead
    // of the single nearest, otherwise we fled from one guard straight
    // into another standing on our escape path.
    // 1.5. Medkit detour (off unless medkitHpPct > 0).
    if (this.medkitHpPct > 0 && hpPct < this.medkitHpPct && view.pickups) {
      let kit = null;
      let kd = this.pickupRange;
      for (const k of view.pickups) {
        if (!k.alive || k.kind !== 'heal') continue;
        const d = Math.hypot(k.pos.x - p.pos.x, k.pos.y - p.pos.y);
        if (d < kd) { kd = d; kit = k; }
      }
      if (kit) {
        const inv = 1 / Math.max(1, kd);
        return { moveX: (kit.pos.x - p.pos.x) * inv, moveY: (kit.pos.y - p.pos.y) * inv, attack: false, dash: false };
      }
    }

    // 1.6. Horse detour: a ride is worth a short walk.
    if (this.horseRange > 0 && !p.horseHp && view.pickups) {
      let horse = null;
      let hd = this.horseRange;
      for (const k of view.pickups) {
        if (!k.alive || k.kind !== 'mount') continue;
        const d = Math.hypot(k.pos.x - p.pos.x, k.pos.y - p.pos.y);
        if (d < hd) { hd = d; horse = k; }
      }
      if (horse) {
        const inv = 1 / Math.max(1, hd);
        return { moveX: (horse.pos.x - p.pos.x) * inv, moveY: (horse.pos.y - p.pos.y) * inv, attack: false, dash: false };
      }
    }

    const swarmCount = this._guardsWithin(p, view.guards, this.swarmRadius);
    let retreat = hpPct < this.lowHpPct || (hpPct < this.swarmHpPct && swarmCount >= this.swarmCount);
    // Hysteresis: without it the bot turns back the moment HP crosses
    // lowHpPct and walks straight into the next trade.
    if (this.resumeHpPct > 0) {
      if (retreat) this._retreating = true;
      else if (this._retreating && hpPct < this.resumeHpPct) retreat = true;
      else this._retreating = false;
    }
    if (retreat) {
      const flee = this._guardClusterFleeDir(p, view.guards, this.fleeRange);
      if (flee) {
        const dash = p.dashCooldownTimer <= 0 && flee.nearest < this.fleeDashDist;
        const dir = this._steerFlee(flee, p);
        return { moveX: dir.x, moveY: dir.y, attack: false, dash };
      }
      const loot = nearestAlive(p.pos, view.loots);
      if (loot) {
        const dx = loot.entity.pos.x - p.pos.x;
        const dy = loot.entity.pos.y - p.pos.y;
        const d = Math.max(1, loot.distance);
        return { moveX: dx / d, moveY: dy / d, attack: false, dash: false };
      }
      // No threats, no loot — fall through to engage and try to earn gold.
    }

    // 2.5. Shop detour. Before robbing the last caravan of the wave,
    // duck into the in-world shop if we have spare gold and haven't
    // already burned this wave's shop pick. Threats are ignored — the
    // retreat block above already aborts at low HP, so taking some
    // chip damage on the way to the shop is acceptable.
    // Reset trip-done flag at the start of every new wave.
    if (this._shopTripWave !== view.wave) {
      this._shopTripWave = view.wave;
      this._shopTripDone = false;
    }
    if (
      view.shop &&
      view.shopPickAvailable !== false &&
      !this._shopTripDone &&
      p.gold >= this.shopMinGold &&
      view.caravans &&
      view.caravans.filter(c => c.alive).length === 1 &&
      hpPct >= this.medHpPct
    ) {
      const shopPos = view.shop.pos;
      const sdx = shopPos.x - p.pos.x;
      const sdy = shopPos.y - p.pos.y;
      const sd = Math.sqrt(sdx * sdx + sdy * sdy);
      const interactRange = view.shop.interactRange || 56;
      if (sd < interactRange * 0.9) {
        this._shopTripDone = true;
        return { moveX: 0, moveY: 0, attack: false, dash: false, enterShop: true };
      }
      const inv = 1 / Math.max(1, sd);
      const dash = !this.dashReserve && p.dashCooldownTimer <= 0 && sd > this.dashDistance;
      return { moveX: sdx * inv, moveY: sdy * inv, attack: false, dash };
    }

    // 3. Pick a target: threatening guard in aggro range, else caravan,
    // else any guard, else loot.
    let target = null;
    let isLoot = false;
    if (threatGuard && threatGuard.distance < this.aggroRange) {
      target = threatGuard;
    } else {
      const caravan = nearestAlive(p.pos, view.caravans);
      if (caravan) {
        target = caravan;
      } else if (threatGuard) {
        target = threatGuard;
      } else {
        const loot = nearestAlive(p.pos, view.loots);
        if (loot) {
          target = loot;
          isLoot = true;
        }
      }
    }

    if (!target) {
      return { moveX: 0, moveY: 0, attack: false, dash: false };
    }

    const dx = target.entity.pos.x - p.pos.x;
    const dy = target.entity.pos.y - p.pos.y;
    const d = target.distance;

    if (!isLoot) {
      const reach = p.radius + p.attackRange + (target.entity.radius || 12);
      const e = target.entity;
      if (this.spacing && e.def && !e.def.ranged) {
        const act = this._spacedEngage(p, e, dx, dy, d, reach);
        if (act) return act;
      }
      if (d < reach * this.reachFrac) {
        // Strafe-attack in the neutral HP band against melee guards: the
        // player can move and swing on the same frame, so a perpendicular
        // step bleeds less HP on the trade without losing DPS. Archers
        // outrange melee so strafing doesn't help — stand and swing.
        const targetType = target.entity.type;
        const isMeleeGuard = targetType === 'basic' || targetType === 'armored';
        if (hpPct < this.medHpPct && isMeleeGuard) {
          const inv = 1 / Math.max(1, d);
          return {
            moveX: -dy * inv,
            moveY: dx * inv,
            attack: true,
            dash: false,
          };
        }
        return { moveX: 0, moveY: 0, attack: true, dash: false };
      }
    }

    const dash = !this.dashReserve && !isLoot && d > this.dashDistance && p.dashCooldownTimer <= 0;
    return {
      moveX: d > 0 ? dx / d : 0,
      moveY: d > 0 ? dy / d : 0,
      attack: false,
      dash,
    };
  }

  decideShop(view) {
    if (!view.offer || view.offer.length === 0) return { action: 'skip' };
    const costs = view.costs || [];
    const canAfford = (i) => (costs[i] || 0) <= view.gold;
    // Pick the highest-priority affordable card not in the avoid list.
    for (const id of this.preferCards) {
      if (this.avoid.has(id)) continue;
      const idx = view.offer.findIndex((c, i) => c.id === id && canAfford(i));
      if (idx >= 0) return { action: 'pick', index: idx };
    }
    // Fallback: cheapest affordable non-avoided card.
    let best = -1, bestCost = Infinity;
    for (let i = 0; i < view.offer.length; i++) {
      if (this.avoid.has(view.offer[i].id)) continue;
      if (canAfford(i) && (costs[i] || 0) < bestCost) {
        bestCost = costs[i] || 0;
        best = i;
      }
    }
    return best >= 0 ? { action: 'pick', index: best } : { action: 'skip' };
  }

  // Return the closest projectile that is both near the player AND aimed
  // roughly at the player. Dot-product filter avoids dashing out of the
  // way of arrows that were never going to hit anyway.
  _incomingProjectile(projectiles, player) {
    if (!projectiles || projectiles.length === 0) return null;
    let best = null;
    let bestTime = Infinity;
    for (const proj of projectiles) {
      if (!proj.alive) continue;
      const dx = player.pos.x - proj.pos.x;
      const dy = player.pos.y - proj.pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > this.projectileWarnDist) continue;
      const inv = 1 / Math.max(0.001, dist);
      const dot = proj.dir.x * dx * inv + proj.dir.y * dy * inv;
      if (dot < 0.7) continue; // not actually coming at us
      // Arrows we must dodge (stickyDodge / fireDodge) outrank plain ones:
      // sticky first, then fire, then the soonest hit.
      const timeToHit = dist / CONST.PROJECTILE_SPEED;
      const rank = this.stickyDodge && proj.sticky ? -2
        : this._mustDodge(proj, player) ? -1 : timeToHit;
      if (timeToHit < this.projectileWarnTime && rank < bestTime) {
        bestTime = rank;
        best = proj;
      }
    }
    return best;
  }

  // Return a unit vector pointing straight out of the most urgent bomb the
  // player is standing in, or null if none threatens. "Most urgent" is the
  // shortest fuse among the bombs whose blast currently covers us — with
  // overlapping markers, the one about to go off is the one to leave.
  //
  // A margin is added to the blast radius so the AI clears the edge rather
  // than hovering exactly on it, and bombs whose fuse is longer than the
  // time needed to walk out are ignored so it doesn't panic early.
  _threateningBomb(bombs, player) {
    if (!bombs || bombs.length === 0) return null;
    const margin = 12;
    let best = null;
    let bestTimer = Infinity;
    for (const bomb of bombs) {
      if (!bomb.alive) continue;
      const dx = player.pos.x - bomb.pos.x;
      const dy = player.pos.y - bomb.pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const reach = bomb.radius + player.radius + margin;
      if (dist > reach) continue;
      if (bomb.timer < bestTimer) {
        bestTimer = bomb.timer;
        // Standing dead centre gives no escape direction — pick one.
        best = dist > 0.001
          ? { x: dx / dist, y: dy / dist }
          : { x: 1, y: 0 };
      }
    }
    return best;
  }

  // Nearest lancer that is winding up or mid-lunge with us in its lane.
  // Returns { dir } — the charge direction, so callers can step
  // perpendicular to it. Models what the on-screen telegraph gives a human:
  // the lane is visible for the entire windup.
  _incomingCharge(guards, player) {
    if (!guards || guards.length === 0) return null;
    let best = null;
    let bestDist = Infinity;
    for (const g of guards) {
      if (!g.alive || !g.chargePhase || !g.chargeDir) continue;
      const dx = player.pos.x - g.pos.x;
      const dy = player.pos.y - g.pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      // Only worth reacting to inside the lunge's actual reach. Derived
      // from the constants rather than hardcoded, so retuning the charge
      // can't leave the AI dodging at the wrong distance and quietly
      // shifting every sim baseline.
      const reach = CONST.LANCER_CHARGE_SPEED * CONST.LANCER_CHARGE_DURATION + 40;
      if (dist > reach || dist >= bestDist) continue;
      const inv = 1 / Math.max(0.001, dist);
      // Are we in the lane? Generous cone — the lancer tracks us through
      // the windup, so being roughly in front is enough to matter.
      const dot = g.chargeDir.x * dx * inv + g.chargeDir.y * dy * inv;
      if (dot < 0.65) continue;
      bestDist = dist;
      best = { dir: g.chargeDir };
    }
    return best;
  }

  // Score each alive guard on "how badly does this one hurt me right now"
  // and return the worst offender. Archers weigh 3× because they chip from
  // outside melee reach; armored weigh 2× because they soak hits. Distance
  // is a soft divisor so far threats still matter but close ones win ties.
  // True if any guard or caravan is inside the player's swing arc right
  // now. Used by the dodge branch to keep attacking through archer volleys.
  _meleeTargetInReach(player, guards, caravans) {
    const reachBase = player.radius + player.attackRange;
    const check = (list) => {
      if (!list) return false;
      for (const e of list) {
        if (!e.alive) continue;
        const dx = e.pos.x - player.pos.x;
        const dy = e.pos.y - player.pos.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        const reach = reachBase + (e.radius || 12);
        if (d < reach * this.reachFrac) return true;
      }
      return false;
    };
    return check(guards) || check(caravans);
  }

  // Count alive guards within `range` px of the player. Used to gate
  // multi-threat retreat — a single guard is a trade, a cluster is a
  // death.
  _guardsWithin(player, guards, range) {
    if (!guards) return 0;
    let n = 0;
    const r2 = range * range;
    for (const g of guards) {
      if (!g.alive) continue;
      const dx = g.pos.x - player.pos.x;
      const dy = g.pos.y - player.pos.y;
      if (dx * dx + dy * dy <= r2) n++;
    }
    return n;
  }

  // Build a flee direction from all guards within `range`, weighted by
  // inverse distance — closer guards push harder. Returns null if no
  // guard is in range. Also returns the nearest guard's distance so the
  // caller can decide whether to burn a dash.
  _guardClusterFleeDir(player, guards, range) {
    if (!guards || guards.length === 0) return null;
    let fx = 0, fy = 0, nearest = Infinity;
    let anyInRange = false;
    for (const g of guards) {
      if (!g.alive) continue;
      const dx = player.pos.x - g.pos.x;
      const dy = player.pos.y - g.pos.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d > range) continue;
      anyInRange = true;
      if (d < nearest) nearest = d;
      const w = 1 / Math.max(20, d);
      fx += (dx / Math.max(0.001, d)) * w;
      fy += (dy / Math.max(0.001, d)) * w;
    }
    if (!anyInRange) return null;
    const len = Math.sqrt(fx * fx + fy * fy);
    if (len < 0.001) return { x: 0, y: -1, nearest };
    return { x: fx / len, y: fy / len, nearest };
  }

  // An arrow dodged at any HP: sticky (stickyDodge), or fire while the burn
  // wouldn't just be refreshed (fireDodge).
  _mustDodge(proj, player) {
    if (this.stickyDodge && proj.sticky) return true;
    return this.fireDodge && proj.fire && (player.burnTimer || 0) <= this.fireRefreshSec;
  }

  // Flee direction with the optional sideways mix (circle-kite) and wall
  // avoidance. Identity when both are off.
  _steerFlee(flee, player) {
    if (this.tangentFlee <= 0 && this.wallRepel <= 0) return flee;
    let x = flee.x;
    let y = flee.y;
    if (this.tangentFlee > 0) {
      // Of the two perpendiculars, take the one pointing toward the middle
      // of the world, so circling doesn't run us into a corner.
      let tx = -y;
      let ty = x;
      const cx = CONST.WORLD_W / 2 - player.pos.x;
      const cy = CONST.WORLD_H / 2 - player.pos.y;
      if (tx * cx + ty * cy < 0) { tx = -tx; ty = -ty; }
      x = (1 - this.tangentFlee) * x + this.tangentFlee * tx;
      y = (1 - this.tangentFlee) * y + this.tangentFlee * ty;
    }
    if (this.wallRepel > 0) {
      const r = this.wallRepel;
      const push = d => (d < r ? 1 - d / r : 0);
      x += push(player.pos.x) - push(CONST.WORLD_W - player.pos.x);
      y += push(player.pos.y) - push(CONST.WORLD_H - player.pos.y);
    }
    const len = Math.hypot(x, y);
    return len > 1e-6 ? { x: x / len, y: y / len, nearest: flee.nearest } : flee;
  }

  // Spacing vs a melee guard: our reach is longer than his, so fight from
  // the band between the two. Facing follows movement and the swing arc is
  // in front, so a swing is always paired with a step toward him; backing
  // off happens only while our swing recharges. Returns null when there is
  // no band (his reach ≥ ours) or he is out of our reach and our swing is
  // ready — the caller's normal approach handles that.
  _spacedEngage(p, g, dx, dy, d, reach) {
    const guardReach = g.attackRange + p.radius;
    const safe = guardReach + this.spacingMargin;
    const myReach = reach * this.reachFrac;
    if (myReach <= safe) return null;
    const inv = 1 / Math.max(1, d);
    const tx = dx * inv;
    const ty = dy * inv;
    const guardRecharging = this.punishWindow > 0 && g.attackTimer > this.punishWindow;
    const minD = guardRecharging ? 0 : safe;
    if (p.attackTimer <= 0) {
      return d < myReach ? { moveX: tx, moveY: ty, attack: true, dash: false } : null;
    }
    if (d < minD) return { moveX: -tx, moveY: -ty, attack: false, dash: false };
    if (d > myReach) return { moveX: tx, moveY: ty, attack: false, dash: false };
    return { moveX: 0, moveY: 0, attack: false, dash: false };
  }

  _mostThreateningGuard(player, guards) {
    if (!guards || guards.length === 0) return null;
    let best = null;
    let bestScore = -Infinity;
    for (const g of guards) {
      if (!g.alive) continue;
      const dx = g.pos.x - player.pos.x;
      const dy = g.pos.y - player.pos.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      const typeWeight = this.threatWeights[g.type] ?? 1;
      const dmg = g.damage || 8;
      // Distance floor 40 keeps the denominator from exploding for guards
      // right on top of us — without it, a basic at 10px beats an archer
      // at 200px by 20×, which is right (melee is the immediate threat).
      let score = (dmg * typeWeight * 100) / Math.max(this.threatDistFloor, d);
      if (this.woundedBias > 0 && g.hp > 0) score *= (g.maxHp / g.hp) ** this.woundedBias;
      if (score > bestScore) {
        bestScore = score;
        best = { entity: g, distance: d };
      }
    }
    return best;
  }
}

// NovicePolicy — SmartPolicy with degraded inputs to mimic a first-time
// human player. Targets prod telemetry mortality curve for 1.2.40+ first
// sessions: W4 ~15%, W5 ~10%, W6 ~9%, W7 ~13%, W8 ~25%, W9 ~14%.
//
// Current calibration vs target (300 runs, seed 42):
//   wave  novice%  prod%   notes
//    4      5      15     under (see structural note below)
//    5      1      10     under
//    6      8       9     match
//    7      9      13     close
//    8     22      25     close
//    9     41      14     overshoots — see structural note
//
// STRUCTURAL NOTE: sim wave durations run 2-3× longer than prod for
// reasons unrelated to NovicePolicy (the same gap shows in SmartPolicy).
// Smart-AI clears W6 in ~80 sec, prod first-timers in ~34 sec. Likely
// causes: smart-AI engages every threat in aggro range vs real players
// quickly grabbing loot and moving on; smart-AI's per-engagement strafe
// loop is slower than a human just holding attack. Net effect on novice:
// it eats 2-3× more damage per wave than prod, which drains HP faster
// than maxHp/sustain cards can replenish, inflating W9+ mortality.
//
// What works: relative slope and the W4/W7 "introduction cliff" pattern
// reproduce. Use novice for testing rebalance directions (e.g. "will
// nerfing armored on W4-W5 help first-timers"), NOT for absolute survival
// rate predictions.
//
// Degradations vs SmartPolicy defaults:
// - Card pick order: attackRange/damage/cooldown/speed/maxHp first
//   (matches observed prod first-pick distribution; maxHp NOT first means
//   novice doesn't get the W4-end full-heal buffer most runs)
// - Skip shop trips (shopMinGold = 99999)
// - Brain-fart events: per-tick chance of either freezing in place
//   (multi-tick streak — the panic state) or charging the nearest guard
//   (the "I'll kill this one first" impulse). Scales by wave: highest on
//   W4-W5 (controls unfamiliarity), bump on W7 (archer debut), near-zero
//   on W9-W10 (prod survivors are calm).
//
// Tune all thresholds via constructor options. Re-run with `--policy
// novice --count 300 --max-waves 15 --seed 42` after edits.
export class NovicePolicy extends SmartPolicy {
  constructor(options = {}) {
    super({ ...options, name: options.name || 'novice' });

    // Reaction degradation: novice dodges arrows reliably when they
    // register (real first-timers don't get crushed by archers harder than
    // they get crushed by armored — keep arrow handling near-smart).
    this.projectileWarnDist = options.projectileWarnDist ?? 85;
    this.projectileWarnTime = options.projectileWarnTime ?? 0.40;
    // Mid-wave self-preservation: cautious — real first-timers DO retreat
    // when scared. Above smart's defaults so the per-wave DMG matches prod
    // (Smart's 0.3/0.7 yields 2-3× more damage than real first-timers take).
    this.lowHpPct = options.lowHpPct ?? 0.40;
    this.medHpPct = options.medHpPct ?? 0.75;
    // Dodge arrows aggressively — main differentiator from smart is melee, not arrows.
    this.arrowDodgeHpPct = options.arrowDodgeHpPct ?? 0.90;
    // Match Smart's threat ring. Narrower made waves LONGER (novice flees
    // guards instead of killing them, gets chipped on the run, takes more
    // damage AND more time). Engagement = faster wave end. NOTE: sim wave
    // durations still run 2-3× longer than prod even at Smart-like aggro;
    // structural sim vs live-game gap (see comment in `decidePlaying`).
    this.aggroRange = options.aggroRange ?? 260;
    // Dash trigger distance for late waves; early waves disabled entirely
    // (see `_dashLearnedByWave`).
    this.dashDistance = options.dashDistance ?? 360;
    // Wave at which novice "learns" dash exists. Before this, dash is
    // never triggered — real first-timers often don't discover dash for
    // 3-4 waves, which inflates chip damage during armored introduction.
    this._dashLearnedByWave = options.dashLearnedByWave ?? 6;
    // Card priority: top-5 matches prod first-pick distribution
    // (attackRange most common — 16/49 in 1.2.40+ first sessions).
    // Sustain (lifesteal/regen) at positions 3-4 — slightly above the
    // observed prod ratio (lifesteal 34, regen 37 of 49) so the sim's
    // longer waves (2-3× prod) don't drain HP faster than novice can
    // replenish via passive healing.
    this.preferCards = options.prefer || [
      'attackRange', 'damage', 'lifesteal', 'regen', 'cooldown',
      'maxHp', 'speed', 'dashCooldown', 'thorns',
    ];
    // Skip the shop detour entirely — novice doesn't plan around it.
    this.shopMinGold = options.shopMinGold ?? 99999;

    // Base brain-fart rate. Per-wave multiplier in decidePlaying scales it.
    this.brainFartRate = options.brainFartRate ?? 0.08;
    // Freeze streaks REMOVED — they bloated wave duration to 3-4× prod
    // (109 sec on W6 vs prod 34 sec), which doubled per-wave damage
    // because novice spent twice as long under fire. Brain-farts now
    // single-tick and "charge wrong target" instead of "stand still".
  }

  decidePlaying(view) {
    const p = view.player;
    const lockDash = view.wave < this._dashLearnedByWave;

    const incoming = this._incomingProjectile(view.projectiles, p);
    // Brain-fart rate scales with wave: first-timers panic hardest during
    // controls-unfamiliarity (W1-W4) and the "what is this new enemy" beats
    // (W7 = archers). Tapers sharply by W8 where survivors are calmer.
    let waveMul;
    if (view.wave <= 5) waveMul = 2.0;        // early panic (armored debut W4, boss W5)
    else if (view.wave === 6) waveMul = 0.3;
    else if (view.wave === 7) waveMul = 0.7;  // archers debut
    else if (view.wave === 8) waveMul = 0.3;
    else if (view.wave <= 10) waveMul = 0.02; // W9-W10 dip in prod data
    else waveMul = 0.15;                      // later cliffs return

    // Use the simulator's seeded rng when present so brain-farts are
    // reproducible — otherwise two seed=42 runs would diverge on the
    // first random fart and break combo-scan baselines.
    const rand = view.rng ? view.rng.next : Math.random;
    if (!incoming && rand() < this.brainFartRate * waveMul) {
      // Brain-fart = "charge nearest guard for one tick" (the "I'll kill
      // this one first" novice impulse). Single-tick only — no freezing,
      // no streak — so wave durations stay near prod values (the live game
      // ticks the wave forward whether or not the player is doing useful
      // things; long stalls bloat damage taken and break the match).
      const nearestGuard = nearestAlive(p.pos, view.guards);
      const nearestCaravan = nearestAlive(p.pos, view.caravans);
      const target = nearestGuard || nearestCaravan;
      if (target) {
        const dx = target.entity.pos.x - p.pos.x;
        const dy = target.entity.pos.y - p.pos.y;
        const d = target.distance;
        const reach = p.radius + p.attackRange + (target.entity.radius || 12);
        if (d < reach * 0.95) {
          return { moveX: 0, moveY: 0, attack: true, dash: false };
        }
        return {
          moveX: d > 0 ? dx / d : 0,
          moveY: d > 0 ? dy / d : 0,
          attack: false,
          dash: false,
        };
      }
    }

    const decision = super.decidePlaying(view);
    if (lockDash && decision.dash) decision.dash = false;
    return decision;
  }
}

// RunnerPolicy — just runs toward caravans ignoring guards until forced.
// Useful to stress-test boss / elite damage output.
export class RunnerPolicy extends AIPolicy {
  constructor(options = {}) {
    super(options.name || 'runner');
    this.aggroRange = options.aggroRange ?? 60;
  }

  decidePlaying(view) {
    const p = view.player;
    const caravan = nearestAlive(p.pos, view.caravans);
    const guard = nearestAlive(p.pos, view.guards);

    // Only swing at guards if we literally collide with one.
    if (guard && guard.distance < this.aggroRange) {
      const dx = guard.entity.pos.x - p.pos.x;
      const dy = guard.entity.pos.y - p.pos.y;
      const d = guard.distance;
      const reach = p.radius + p.attackRange + (guard.entity.radius || 12);
      if (d < reach * 0.95) {
        return { moveX: 0, moveY: 0, attack: true, dash: false };
      }
      return {
        moveX: d > 0 ? dx / d : 0,
        moveY: d > 0 ? dy / d : 0,
        attack: false,
        dash: p.dashCooldownTimer <= 0,
      };
    }

    if (!caravan) {
      const loot = nearestAlive(p.pos, view.loots);
      if (!loot) return { moveX: 0, moveY: 0, attack: false, dash: false };
      const dx = loot.entity.pos.x - p.pos.x;
      const dy = loot.entity.pos.y - p.pos.y;
      const d = loot.distance;
      return {
        moveX: d > 0 ? dx / d : 0,
        moveY: d > 0 ? dy / d : 0,
        attack: false,
        dash: false,
      };
    }
    const dx = caravan.entity.pos.x - p.pos.x;
    const dy = caravan.entity.pos.y - p.pos.y;
    const d = caravan.distance;
    const reach = p.radius + p.attackRange + (caravan.entity.radius || 14);
    if (d < reach * 0.95) {
      return { moveX: 0, moveY: 0, attack: true, dash: false };
    }
    return {
      moveX: d > 0 ? dx / d : 0,
      moveY: d > 0 ? dy / d : 0,
      attack: false,
      dash: d > 300 && p.dashCooldownTimer <= 0,
    };
  }

  decideShop(view) {
    if (!view.offer || view.offer.length === 0) return { action: 'skip' };
    const costs = view.costs || [];
    for (let i = 0; i < view.offer.length; i++) {
      if ((costs[i] || 0) <= view.gold) return { action: 'pick', index: i };
    }
    return { action: 'skip' };
  }
}

// HumanPolicy — the evolved SmartPolicy (psmart genes) with a human's
// reaction time, to stand in for a real player when balancing (calibrated
// so its median death wave matches the telemetry's).
// Threat perception lags, aim doesn't: it decides every tick and tracks
// guards where they are (people lead a moving target), but sees a guard's
// strike readiness and a lancer's windup as they were `reactionSec` ago,
// arrows and bombs where they were then, and an arrow or bomb thrown less
// than reactionSec ago not at all. Its own state is current.
// Mistakes: `mistakeRate` times a second on average it overcommits — for
// `mistakeSec` charges the nearest guard and swings, ignoring threats (the
// classic human "one more hit"). Drawn from the simulator's seeded rng, so
// runs stay reproducible; rate 0 draws nothing.
// No randomness; reactionSec 0 ≡ psmart with the same genes.
const HUMAN_TICK = 1 / 60; // the fixed step of the game and the simulator
const GUARD_THREAT_KEYS = ['attackTimer', 'chargePhase', 'chargeTimer'];
const MOVING_KEYS = ['pos', 'vel'];

export class HumanPolicy extends AIPolicy {
  constructor({ reactionSec = 0, mistakeRate = 0, mistakeSec = 0.25, ...genes } = {}) {
    super('human');
    this.mistakeRate = mistakeRate;
    this.mistakeTicks = Math.max(1, Math.round(mistakeSec / HUMAN_TICK));
    this._mistake = 0; // ticks of the current overcommit left
    this.inner = new SmartPolicy({ name: 'psmart', ...genes });
    this.lagTicks = Math.round(reactionSec / HUMAN_TICK);
    // Plain Maps, not WeakMaps: wave snapshots (deepClone) rebuild Maps but
    // not WeakMaps. Entries of entities gone from view are pruned.
    this._hist = new Map();   // entity → { tick, states: its last lagTicks + 1 perceived states }
    this._ghosts = new Map(); // entity → stable stand-in seen by the bot
    this._tick = 0;
  }

  // The entity as perceived: a stand-in that inherits everything from it
  // except the `keys` it had lagTicks ago (null while younger than that).
  _seen(e, keys) {
    let rec = this._hist.get(e);
    if (!rec) this._hist.set(e, rec = { tick: 0, states: [] });
    rec.tick = this._tick;
    const h = rec.states;
    const now = {};
    for (const k of keys) {
      const v = e[k];
      now[k] = v && typeof v.copy === 'function' ? v.copy() : v;
    }
    h.push(now);
    if (h.length > this.lagTicks + 1) h.shift();
    if (h.length <= this.lagTicks) return null;
    let g = this._ghosts.get(e);
    if (!g) this._ghosts.set(e, g = Object.create(e));
    Object.assign(g, h[0]);
    return g;
  }

  decidePlaying(view) {
    if (this.mistakeRate > 0) {
      const rand = view.rng ? view.rng.next : Math.random;
      if (this._mistake === 0 && rand() < this.mistakeRate * HUMAN_TICK) this._mistake = this.mistakeTicks;
      if (this._mistake > 0) {
        this._mistake--;
        const move = this._overcommit(view);
        if (move) return move;
      }
    }
    if (this.lagTicks === 0) return this.inner.decidePlaying(view);
    if (++this._tick % 120 === 0) {
      for (const [e, rec] of this._hist) {
        if (rec.tick < this._tick - 1) { this._hist.delete(e); this._ghosts.delete(e); }
      }
    }
    const guards = view.guards.map(g => this._seen(g, GUARD_THREAT_KEYS) || g);
    const projectiles = view.projectiles.map(x => this._seen(x, MOVING_KEYS)).filter(Boolean);
    const bombs = (view.bombs || []).map(x => this._seen(x, MOVING_KEYS)).filter(Boolean);
    return this.inner.decidePlaying({ ...view, guards, projectiles, bombs });
  }

  // Walk into the nearest guard and swing, whatever else is going on.
  _overcommit(view) {
    const p = view.player;
    const t = nearestAlive(p.pos, view.guards);
    if (!t) return null;
    const dx = t.entity.pos.x - p.pos.x;
    const dy = t.entity.pos.y - p.pos.y;
    const d = t.distance;
    const reach = p.radius + p.attackRange + (t.entity.radius || 12);
    if (d < reach * 0.95) return { moveX: 0, moveY: 0, attack: true, dash: false };
    return { moveX: d > 0 ? dx / d : 0, moveY: d > 0 ? dy / d : 0, attack: false, dash: false };
  }

  decideShop(view) {
    return this.inner.decideShop(view);
  }
}

// Registry used by the CLI to look up policies by name. Each entry is a
// factory so the simulator can create a fresh instance per run (policies
// may hold mutable state in the future).
export const POLICIES = {
  // SmartPolicy with every tunable exposed (player-brain evolution).
  psmart: (opts) => new SmartPolicy({ name: 'psmart', ...opts }),
  // psmart with a human reaction time (rebalance stand-in for a player).
  human: (opts) => new HumanPolicy(opts),
  greedy: (opts) => new GreedyPolicy(opts),
  'random-cards': (opts) => new RandomCardPolicy(opts),
  runner: (opts) => new RunnerPolicy(opts),
  preference: (opts) => new PreferencePolicy(opts),
  smart: (opts) => new SmartPolicy(opts),
  novice: (opts) => new NovicePolicy(opts),
};
